//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// feed-scheduler imports
import { IContainer, IProps, IState } from "./FeedScheduler.d";
import { feedSchedulerFragments, refetchQuery } from "./FeedScheduler.gql";
import * as s from "./FeedScheduler.styles";
// other imports
import { FeedSchedulerInput } from "@components/feed-scheduler-input";
import { RoomOptionEnum } from "@components/event-scheduler";
// import { UserImage } from '@components/user-image';
// import { TutorialFeedScheduler } from '@components/tutorial-tip';
import { SidebarDialog } from "@clockwise/web-commons/src/components/sidebar-dialog";
import { RangeAndDurationPicker } from "@components/range-and-duration-picker";
import { InfoTip } from "@components/tool-tip";
// state
import {
  makeEventSchedulerViewActive,
  resetScheduledEventIds,
  updateRoomRequired,
  updateCalendarIds,
  updateSchedulingSessionId,
  updateUseAutoSchedule,
  updateDuration,
  updateRange,
} from "@actions/feed-scheduler.actions";
import { setActiveViewEvent } from "@actions/chrome-extension.actions";
import { updateFlagsForUser } from "@mutations";
import { IReduxState } from "@reducers/root.reducer";
// assets
import { scheduleMeetingIcon } from "@img/icons/";
// util
import { logger } from "@util/logger.util";
import { EScheduleRange } from "@util/scheduler.util";
import { H2, A } from "@ui";
import {
  feedSchedulerSelectionStateBlackList,
  isEmailBlackListedForFeedScheduler,
} from "@util/chrome-calendars.util";
import {
  calendarIdsToPersons,
  // calendarIdsToPersonAndCalendarIdsMap,
} from "@util/calendar-id.util";
import { sortDataSources } from "@util/data-source.util";
import {
  FlagNamespaces,
  UserFlags,
  isFlagSet,
  isUserAtLeastXDaysOld,
  validateFlagsErrorable,
} from "@util/flags.util";
// import {
//   niceNameArrayFromPersons,
//   isNameInNiceNamesArray,
// } from '@util/event.util';
import { PostMessageManager } from "@util/post-message.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { createUUID } from "@clockwise/client-commons/src/util/uuid";
// material-ui
import {
  Button,
  DialogActions,
  DialogContent,
  Fab,
  FormControl,
  IconButton,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  Close,
  // EventAvailable,
  Check,
  Business,
  Assistant,
} from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class FeedSchedulerCmpt extends React.Component<IProps, IState> {
  private removeSelectionSchedulerTipTimeout: ReturnType<typeof setTimeout>;
  private removeScheduledEventConfirmationTimeout: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      calendarIds: [], // these are the selected attendees
      calendarsToInitialize: [], // when expanding feed-scheduler, these calendars are the ones that we start with
      chromeCheckedCalendars: [], // these are the calendars checked in the gcal
      showSelectionSchedulerTip: false,
      expanded: false,
      includeRoom: props.roomRequired ? RoomOptionEnum.WithRoom : RoomOptionEnum.WithoutRoom,
      showScheduledEventConfirmation:
        !!props.scheduledCalendarId && !!props.scheduledExternalEventId,
      scheduledCalendarId: props.scheduledCalendarId,
      scheduledExternalEventId: props.scheduledExternalEventId,
      confirmationDialogOpen: false,
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    this.maybeShowSelectionSchedulerTip(nextProps.calendarSelectionStates, nextProps.org);

    if (nextProps.chromeSchedulerButtonClicked !== this.props.chromeSchedulerButtonClicked) {
      if (nextProps.chromeSchedulerEmails.length) {
        this.setState(
          {
            calendarsToInitialize: nextProps.chromeSchedulerEmails,
          },
          () => {
            this.setExpansion(true);
          },
        );
      } else {
        this.setExpansion(true);
      }
    }
  }

  public componentDidMount() {
    clearTimeout(this.removeScheduledEventConfirmationTimeout);
    this.removeScheduledEventConfirmationTimeout = setTimeout(() => {
      this.props.resetScheduledEventIds();
      this.setState({
        showScheduledEventConfirmation: false,
        scheduledCalendarId: "",
        scheduledExternalEventId: "",
      });
    }, 10000);
  }

  public componentWillUnmount() {
    clearTimeout(this.removeSelectionSchedulerTipTimeout);
    clearTimeout(this.removeScheduledEventConfirmationTimeout);
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private maybeShowSelectionSchedulerTip = (
    calendarSelectionStates: IReduxState["webExtension"]["calendarSelectionStates"],
    org: ISchema.IOrg,
  ) => {
    const sortedDataSources = sortDataSources(
      this.props.user.dataSources?.edges ?? [],
      org.domains,
    );
    const syncedPersonalCalendars = sortedDataSources.other.map((ds) => ds.name);
    const userCalId = org && org.primaryOrgEmail;

    const shadowCalendarIds = [];
    if (
      org.shadowCalendarSettingsErrorable &&
      org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
    ) {
      const shadowCalendarId = org.shadowCalendarSettingsErrorable.shadowCalendarId;
      if (shadowCalendarId) {
        shadowCalendarIds.push(shadowCalendarId);
      }
    }
    const blacklist = [
      userCalId,
      ...shadowCalendarIds,
      ...syncedPersonalCalendars,
      ...feedSchedulerSelectionStateBlackList,
    ];

    const chromeCheckedCalendars = Object.keys(calendarSelectionStates).filter(
      (calId) =>
        !!calendarSelectionStates[calId] && !isEmailBlackListedForFeedScheduler(calId, blacklist),
    );

    if (
      JSON.stringify(this.state.chromeCheckedCalendars) !== JSON.stringify(chromeCheckedCalendars)
    ) {
      // refetch persons
      this.props.relay.refetch(
        {
          personCalendarIds: chromeCheckedCalendars,
          orgRelayId: org.id,
        },
        undefined,
        () => {
          // update state
          this.setState({ chromeCheckedCalendars, showSelectionSchedulerTip: true });
        },
        { force: false } as any,
      );
    }
  };

  private maybeSetSeenFeedSchedulerFlag = () => {
    const tipShowing =
      validateFlagsErrorable(this.props.user.chromeExtensionFlagsErrorable) &&
      !isFlagSet(
        UserFlags.Onboarding.FeedScheduler.name,
        this.props.user.chromeExtensionFlagsErrorable as ISchema.IFlags,
      ) &&
      isUserAtLeastXDaysOld(this.props.user, 1);

    if (!tipShowing) {
      return;
    }

    updateFlagsForUser(
      this.props.relay.environment,
      {
        flagNamespace: FlagNamespaces.ChromeExtension,
        flagNames: [UserFlags.Onboarding.FeedScheduler.name],
        enable: true,
      },
      () => null,
      () => null,
    );
  };

  private clearCalendarsAndSetExpanded = () => {
    this.setState(
      {
        calendarsToInitialize: [],
      },
      () => {
        this.setExpansion(true);
      },
    );
  };

  private setExpansion = (expanded: boolean) => {
    this.setState({ expanded });
    this.maybeSetSeenFeedSchedulerFlag();

    // analytics
    const schedulingSessionId = createUUID();
    this.props.updateSchedulingSessionId(schedulingSessionId);
    if (expanded) {
      track(TrackingEvents.SCHEDULING.FEED_SCHEDULER_EXPANDED, {
        schedulingSessionId,
        currentView: this.props.currentView,
      });
    }
  };

  // private closeTip = (e: React.MouseEvent<{}>) => {
  //   this.setState({ showSelectionSchedulerTip: false, calendarsToInitialize: [] });
  //   track(TrackingEvents.SCHEDULING.FEED_SCHEDULER_CALENDAR_SELECTION_TIP_CLOSED, {
  //     schedulingSessionId: this.props.schedulingSessionId,
  //   }); // analytics
  //   e.stopPropagation();
  // }

  private onIncludeRoomChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const includeRoom = e.target.value as RoomOptionEnum;
    this.setState({ includeRoom });
    this.props.updateRoomRequired(includeRoom === RoomOptionEnum.WithRoom);
    track(TrackingEvents.SCHEDULING.FEED_SCHEDULER_INCLUDE_ROOM_CHANGE, {
      includeRoom,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics
  };

  private onSchedulerInputChange = (calendarIds: string[]) => {
    this.setState({ calendarIds });
    this.props.updateCalendarIds(calendarIds);
  };

  private goToEventScheduler = (autoSchedule = false) => {
    const event = autoSchedule
      ? TrackingEvents.SCHEDULING.FEED_SCHEDULER_AUTO_SCHEDULE_CLICKED
      : TrackingEvents.SCHEDULING.FEED_SCHEDULER_CHOOSE_TIME_CLICKED;
    track(event, {
      schedulingSessionId: this.props.schedulingSessionId,
      calendarIds: this.state.calendarIds,
    }); // analytics
    if (autoSchedule) {
      this.props.updateUseAutoSchedule(true);
    }
    this.props.makeEventSchedulerViewActive(true);
  };

  private closeEventScheduledConfirmation = () => {
    clearTimeout(this.removeScheduledEventConfirmationTimeout);
    this.props.resetScheduledEventIds();
    this.setState({
      showScheduledEventConfirmation: false,
      scheduledCalendarId: "",
      scheduledExternalEventId: "",
    });
  };

  private goToEditEvent = (scheduledExternalEventId: string, scheduledCalendarId: string) => {
    this.closeEventScheduledConfirmation();
    track(
      TrackingEvents.SCHEDULING.FEED_SCHEDULER_EVENT_SCHEDULED_CONFIRMATION_EDIT_EVENT_CLICKED,
      {
        schedulingSessionId: this.props.schedulingSessionId,
      },
    ); // analytics
    PostMessageManager.goToEventEditPage(scheduledExternalEventId, scheduledCalendarId);
  };

  private goToEventView = (scheduledExternalEventId: string, scheduledCalendarId: string) => {
    this.closeEventScheduledConfirmation();
    track(
      TrackingEvents.SCHEDULING.FEED_SCHEDULER_EVENT_SCHEDULED_CONFIRMATION_EVENT_VIEW_CLICKED,
      {
        schedulingSessionId: this.props.schedulingSessionId,
      },
    ); // analytics

    logger.debug("Setting ActiveViewEvent from FeedSchedulerCmpt", {
      externalEventId: scheduledExternalEventId,
      calendarId: scheduledCalendarId,
    });

    this.props.setActiveViewEvent(scheduledExternalEventId, scheduledCalendarId, false);
  };

  private maybeCloseExpansion = () => {
    if (this.state.calendarIds.length > 0) {
      return this.setState({ confirmationDialogOpen: true });
    }
    this.setExpansion(false);
  };

  private exitDialogAction = (open: boolean) => {
    if (!open) {
      this.setExpansion(open);
    }
    this.setState({ confirmationDialogOpen: false });
  };

  private onRangeDurationChange = (
    minutesDuration: number,
    range: EScheduleRange,
    specifiedDate: Date | null,
  ) => {
    this.props.updateDuration(minutesDuration);
    this.props.updateRange(range, specifiedDate);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderExitConfirmationDialog = () => {
    return (
      <SidebarDialog
        open={this.state.confirmationDialogOpen}
        onClose={() => this.exitDialogAction(true)}
        isChromeExtension={this.props.isChromeExtension}
      >
        <DialogContent>
          <H2>Discard unsaved changes?</H2>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.exitDialogAction(false)} color="primary">
            Discard
          </Button>
          <Button
            // keyboardFocused={true}
            onClick={() => this.exitDialogAction(true)}
          >
            Cancel
          </Button>
        </DialogActions>
      </SidebarDialog>
    );
  };

  private renderScheduledEventConfirmation = () => {
    const { scheduledExternalEventId, scheduledCalendarId } = this.state;
    if (!scheduledExternalEventId || !scheduledCalendarId) {
      logger.error("Missing event info to render confirmation");
      return <span />;
    }

    return (
      <div style={s.confirmation.container}>
        <div style={{ ...s.confirmation.row, marginBottom: 15 }}>
          <Check {...s.confirmation.check} />
          <H2 style={s.confirmation.h2} cw-id="snackbar-message">
            Meeting scheduled
          </H2>
          <IconButton
            style={s.close}
            onClick={() => this.closeEventScheduledConfirmation()}
            cw-id="snackbar-close"
          >
            <Close {...s.icon} />
          </IconButton>
        </div>
        <div style={{ ...s.confirmation.row, width: "100%", justifyContent: "space-between" }}>
          <A
            onClick={() => this.goToEventView(scheduledExternalEventId, scheduledCalendarId)}
            cw-id="snackbar-view-button"
          >
            View meeting
          </A>
          <A
            onClick={() => this.goToEditEvent(scheduledExternalEventId, scheduledCalendarId)}
            cw-id="snackbar-edit-button"
          >
            Edit in calendar
          </A>
        </div>
      </div>
    );
  };

  // private renderSelectionSchedulerTip = () => {
  //   const { showSelectionSchedulerTip, expanded, calendarsToInitialize } = this.state;
  //   if (!showSelectionSchedulerTip || expanded || calendarsToInitialize.length < 1) {
  //     return <span />;
  //   }

  //   const selectedPersons = calendarIdsToPersons(calendarsToInitialize, this.props.org);
  //   const { persons, nonPersonCalendarIds } = calendarIdsToPersonAndCalendarIdsMap(calendarsToInitialize, this.props.org);
  //   const niceNamesArray = niceNameArrayFromPersons(persons, nonPersonCalendarIds, 25, true);

  //   return (
  //     <div style={s.tipContainer} onClick={() => this.setExpansion(true)}>
  //       <div style={s.arrow.background}><div style={s.arrow.foreground}></div></div>
  //       <IconButton style={s.close} onClick={e => this.closeTip(e)}>
  //         <Close {...s.icon} />
  //       </IconButton>
  //       <div style={s.tipHeader}>
  //         <Calendar {...s.calendar} />
  //         {
  //           selectedPersons
  //           .slice(0, 8) // grab first few to render
  //           .map((p) => {
  //             return p.profile &&
  //               <UserImage
  //                 key={p.primaryEmail}
  //                 style={s.avatar}
  //                 imgUrl={p.profile.externalImageUrl}
  //                 name={p.profile.givenName}
  //                 />;
  //           },
  //         )}
  //       </div>
  //       <div style={s.tipBody}>
  //         <span>Schedule a meeting with </span>
  //         {niceNamesArray.map((name, i) => {
  //           return (
  //             <span
  //               key={`${name}${i}`}
  //               style={isNameInNiceNamesArray(name) ? s.bold : {}}
  //             >
  //               {name}
  //             </span>
  //           );
  //         })}
  //         <span>?</span>
  //       </div>
  //     </div>
  //   );
  // }

  private renderButtons = () => {
    const { includeRoom } = this.state;
    const autoScheduleBtn = (
      <Button
        color="primary"
        variant="contained"
        classes={{ root: this.props.classes.autoscheduleBtn }}
        onClick={() => this.goToEventScheduler(true)}
        fullWidth
        cw-id="find-best-time-button"
      >
        <Assistant className="icon" />
        Find the best time
      </Button>
    );

    return (
      <div style={{ width: "100%" }}>
        <InfoTip
          targetElement={autoScheduleBtn}
          text={
            <div>
              Allow Clockwise to find the best time <br />{" "}
              {includeRoom === RoomOptionEnum.WithRoom ? "and room" : ""} for you
            </div>
          }
          position={"top"}
          tipType={"info"}
        />
        <Button
          variant="text"
          classes={{ root: this.props.classes.chooseATimeBtn }}
          onClick={() => this.goToEventScheduler()}
          fullWidth
          cw-id="choose-time-button"
        >
          Choose a time
        </Button>
      </div>
    );
  };

  private renderExpanded = () => {
    const { calendarsToInitialize, chromeCheckedCalendars } = this.state;
    const initializedPersons = calendarIdsToPersons(calendarsToInitialize, this.props.org);
    const chromeCheckedPersons = calendarIdsToPersons(chromeCheckedCalendars, this.props.org);

    return (
      <div style={s.expanded}>
        <div style={s.header}>
          <H2 style={{ marginBottom: 0 }}>New meeting</H2>
          <IconButton
            aria-label="Close"
            cw-id="feed-scheduler-close-button"
            style={s.close}
            onClick={() => this.maybeCloseExpansion()}
          >
            <Close {...s.icon} />
          </IconButton>
        </div>
        <FeedSchedulerInput
          environment={this.props.relay.environment}
          queryInput={""}
          orgId={this.props.org.id}
          excludeCalendars={[]}
          initialPersons={initializedPersons}
          selectedPersons={chromeCheckedPersons}
          onChange={this.onSchedulerInputChange}
        />
        <RangeAndDurationPicker onChange={this.onRangeDurationChange} />
        <div style={s.room.container}>
          <Business {...s.businessIcon} />
          <FormControl>
            <Select
              value={this.state.includeRoom}
              onChange={this.onIncludeRoomChange}
              name="Include Room"
              inputProps={{
                name: "Duration",
                id: "cw-include-room-picker",
              }}
              disableUnderline
              cw-id="meeting-room-picker"
            >
              <MenuItem value={RoomOptionEnum.WithRoom}>With a meeting room</MenuItem>
              <MenuItem value={RoomOptionEnum.WithoutRoom}>Without a meeting room</MenuItem>
            </Select>
          </FormControl>
        </div>
        {this.renderButtons()}
      </div>
    );
  };

  private renderCollapsed = () => {
    return (
      <Fab
        variant="extended"
        className={this.props.classes.fab}
        style={{ justifyContent: "space-between", textAlign: "left" }}
        onClick={() => this.clearCalendarsAndSetExpanded()}
        cw-id="sidebar-schedule-meeting"
      >
        <img src={scheduleMeetingIcon} style={{ margin: "0 7px" }} />
        <span style={{ flex: 1, margin: "3px 0 0 17px" }}>Schedule meeting</span>
      </Fab>
    );
  };

  public render() {
    const { expanded, showScheduledEventConfirmation } = this.state;

    // if event has been scheduled, render thing
    if (showScheduledEventConfirmation) {
      return this.renderScheduledEventConfirmation();
    }

    return (
      <div>
        {expanded ? (
          <div style={s.expandedContainer}>{this.renderExpanded()}</div>
        ) : (
          <div className={`${this.props.classes.tutorialFabContainer} ${this.props.mode}`}>
            {/* <TutorialFeedScheduler user={this.props.user} /> */}
            {this.renderCollapsed()}
          </div>
        )}
        {expanded ? (
          <div style={s.expandedBackground} onClick={() => this.setExpansion(false)}></div>
        ) : (
          ""
        )}
        {expanded ? <div style={{ height: 50 }}></div> : ""}
        {/* disable selection scheduler tip for now.. */}
        {/* {this.renderSelectionSchedulerTip()} */}
        {this.state.confirmationDialogOpen ? this.renderExitConfirmationDialog() : ""}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const FeedSchedulerStyled = withStyles(s.styles)(FeedSchedulerCmpt) as React.ComponentClass<IProps>;

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    calendarSelectionStates: state.webExtension.calendarSelectionStates,
    eventHasBeenScheduled: state.feedScheduler.eventHasBeenScheduled,
    scheduledExternalEventId: state.feedScheduler.scheduledExternalEventId,
    scheduledCalendarId: state.feedScheduler.scheduledCalendarId,
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
    roomRequired: state.feedScheduler.roomRequired,
    isChromeExtension: state.webExtension.isWebExtension,
    chromeSchedulerEmails: state.webExtension.schedulerEmails,
    chromeSchedulerButtonClicked: state.webExtension.schedulerButtonClicked,
    mode: state.webExtension.mode,
  };
}

const FeedSchedulerRedux = connect(mapStateToProps, {
  makeEventSchedulerViewActive,
  setActiveViewEvent,
  resetScheduledEventIds,
  updateRoomRequired,
  updateCalendarIds,
  updateSchedulingSessionId,
  updateUseAutoSchedule,
  updateDuration,
  updateRange,
})(FeedSchedulerStyled);

//////////////////
// RELAY
//////////////////
export const FeedScheduler = createRefetchContainer<IContainer>(
  FeedSchedulerRedux,
  feedSchedulerFragments,
  refetchQuery,
);
